import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, PackageSearch } from "lucide-react";
import { norm, money } from "@/lib/format";
import { Input } from "@/components/ui/input";

/** Selector de artículo del inventario: trae precio y existencia disponible. */
export const BuscadorArticulo = ({ articulos, valor, onChange, testId = "buscador-articulo" }) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const caja = useRef(null);

  const elegido = articulos.find((a) => a.id === valor) || null;

  useEffect(() => {
    const fuera = (ev) => { if (caja.current && !caja.current.contains(ev.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  const resultados = useMemo(() => {
    const q = norm(texto.trim());
    const lista = q
      ? articulos.filter((a) => [a.codigo, a.nombre, a.categoria, a.talla, a.grado]
          .filter(Boolean).some((v) => norm(v).includes(q)))
      : articulos;
    return lista.slice(0, 40);
  }, [texto, articulos]);

  return (
    <div className="relative" ref={caja} data-testid={testId}>
      <button type="button" onClick={() => { setAbierto(!abierto); setTexto(""); }}
              data-testid={`${testId}-abrir`}
              className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary/50">
        <span className={elegido ? "truncate" : "truncate text-muted-foreground"}>
          {elegido ? (
            <>
              <span className="font-mono text-xs text-muted-foreground">{elegido.codigo}</span>
              <span className="ml-2 font-medium">{elegido.nombre}</span>
              {elegido.talla && <span className="ml-1 text-xs text-muted-foreground">· {elegido.talla}</span>}
            </>
          ) : "Buscar artículo del inventario…"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {abierto && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
          <div className="relative border-b border-border/60 p-2">
            <PackageSearch className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={texto} onChange={(e) => setTexto(e.target.value)}
                   data-testid={`${testId}-input`} className="pl-9"
                   placeholder="Código, nombre, talla o grado…" />
          </div>
          <ul className="max-h-64 overflow-y-auto" data-testid={`${testId}-lista`}>
            {resultados.map((a) => (
              <li key={a.id}>
                <button type="button" data-testid={`opcion-articulo-${a.codigo}`}
                        onClick={() => { onChange(a); setAbierto(false); }}
                        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted">
                  <span className="min-w-0">
                    <span className="block truncate">
                      <span className="font-mono text-xs text-muted-foreground">{a.codigo}</span>
                      <span className="ml-2 font-medium">{a.nombre}</span>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {a.categoria}{a.talla ? ` · talla ${a.talla}` : ""}{a.grado ? ` · ${a.grado}` : ""}
                      {" · "}{money(a.precio)}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${
                      a.existencia <= 0 ? "bg-destructive/10 text-destructive"
                        : a.existencia <= a.minimo ? "bg-accent/20 text-accent-foreground"
                        : "bg-muted text-muted-foreground"}`}>
                      {a.existencia} disp.
                    </span>
                    {a.id === valor && <Check className="h-4 w-4 text-primary" />}
                  </span>
                </button>
              </li>
            ))}
            {resultados.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-muted-foreground"
                  data-testid={`${testId}-vacio`}>
                Ningún artículo coincide. Créelo en Inventario.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
